import { useCallback, useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";
import type { WalkPoint } from "../WalkFeedback";
import { isDocHtml } from "../DocHtml";
import { registerOpenDocument } from "../doc-open-state";
import { formatLongDate, formatMediumDateTime } from "../date-format";
import { docLocale } from "./doc-locale";
import { moveWalkCursorOrb } from "./walk";

// Print support: while the reading mode is open, printing (button or Cmd+P)
// produces a light typographic sheet of JUST the document - folds open for
// the duration, everything else hides via the `doc-printing` root class.
type FoldLike = { open: boolean };
export type PrintRoot = {
  documentElement: { classList: { add(name: string): void; remove(name: string): void } };
  querySelectorAll(selector: string): ArrayLike<FoldLike>;
};

/** "Uložit jako PDF" downloads a real file (Pavel 2026-08-04): the print
 * dialog stamps the URL and title into every header; the worker prints the
 * SAME rendered sheet with a page-number-only footer. Falls back to the
 * print dialog when the endpoint is unavailable. */
async function downloadDocPdf(title: string): Promise<boolean> {
  const sheet = document.querySelector(".doc-sheet");
  if (!sheet) return false;
  let css = "";
  try {
    for (const styleSheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(styleSheet.cssRules)) css += rule.cssText + "\n";
      } catch { /* cross-origin sheet: skip */ }
    }
  } catch { /* stylesheet access blocked: worker gets bare html */ }
  try {
    const response = await fetch("/api/doc-pdf", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ html: sheet.outerHTML, css, title })
    });
    if (!response.ok) return false;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = (response.headers.get("Content-Disposition") ?? "").match(/filename="([^"]+)"/)?.[1] ?? "dokument.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return true;
  } catch {
    return false;
  }
}

export function beginDocPrint(root: PrintRoot): () => void {
  root.documentElement.classList.add("doc-printing");
  const closedFolds = Array.from(root.querySelectorAll(".doc-sheet details:not([open])"));
  for (const fold of closedFolds) fold.open = true;
  return () => {
    root.documentElement.classList.remove("doc-printing");
    for (const fold of closedFolds) fold.open = false;
  };
}

// ---- Chapter navigation (T6a design language) ----
// Chapters come from the RENDERED document (h3.doc-section scan), so every
// view mode gets them for free: plain reader, diff mode, the emails split.
// Headings inside folds or removed diff blocks are body, not chapters.

// Which chapter is "current" for a given scroll position: the last chapter
// whose top sits above the reading line. Pure, exported for tests.
export function activeChapterIndex(tops: number[], readingLine: number): number {
  let active = 0;
  for (let index = 0; index < tops.length; index++) {
    if (tops[index] <= readingLine) active = index;
  }
  return active;
}

export function readerProgressState(scrollTop: number, scrollHeight: number, clientHeight: number): {
  percent: number;
  atEnd: boolean;
} {
  if (clientHeight <= 0) return { percent: 0, atEnd: false };
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  const clampedTop = Math.min(Math.max(0, scrollTop), maxScroll);
  const atEnd = maxScroll <= 4 || clampedTop >= maxScroll - 4;
  const percent = atEnd ? 100 : Math.round((clampedTop / maxScroll) * 100);
  return { percent, atEnd };
}

// Heading text without the decorative chapter number span.
function chapterHeadingTitle(element: Element): string {
  let text = "";
  for (const node of Array.from(element.childNodes)) {
    if (node instanceof Element && node.classList.contains("doc-section-num")) continue;
    text += node.textContent ?? "";
  }
  return text.replace(/\s+/g, " ").trim();
}

// Shared chapter list markup: the desktop rail and the mobile popover render
// the same nav (keyboard-first: plain buttons, aria-current on the active
// chapter). Fewer than two chapters = no nav at all.
export function ChapterList(props: {
  chapters: Array<{ title: string }>;
  active: number;
  label: string;
  onSelect: (index: number) => void;
}) {
  if (props.chapters.length < 2) return null;
  return (
    <nav className="doc-chapter-nav" aria-label={props.label}>
      <ol>
        {props.chapters.map((chapter, index) => (
          <li key={index}>
            <button
              type="button"
              className={index === props.active ? "active" : undefined}
              aria-current={index === props.active ? "true" : undefined}
              onClick={() => props.onSelect(index)}
            >
              <span className="doc-chapter-nav-num">{String(index + 1).padStart(2, "0")}</span>
              <span className="doc-chapter-nav-title">{chapter.title}</span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Full-screen reading mode with ONE panel system for every material kind
// (Pavel 2026-07-24): documents and delivered pages share the same floating
// annotation panel that can PIN as a fixed right column (420px, the page or
// document shrinks beside it) or float as a draggable window, and collapses
// to a pill. Same pin, same states, client and partner alike.

/** "Verze 3 · 4. srpna 2026 · Připravil tým BuddyPro" - the deliverable's
 * cover meta (report canon rule 19: a document carries version, date, author). */
export function docMetaLine(
  t: (key: string) => string,
  version: unknown,
  updatedAt: unknown
): string | undefined {
  const iso = typeof updatedAt === "string" ? updatedAt : "";
  if (!iso) return undefined;
  // F116: the sentence around it comes from `t`, so the date must speak the
  // same language. It used to be "Version 3 · 11. srpna 2026 · Prepared by the
  // BuddyPro team" on every English document.
  const formatted = formatLongDate(iso, docLocale());
  if (!formatted) return undefined;
  return t("sales.doc.meta")
    .replace("{version}", String(version ?? 1))
    .replace("{date}", formatted);
}

export function DocOverlay(props: {
  title: string;
  kicker: string;
  /** Cover anatomy (report canon rule 19): a deliverable carries its version
   * and date. Rendered as a quiet meta line under the title. */
  meta?: string;
  closeLabel: string;
  t: (key: string) => string;
  onClose: () => void;
  children: ReactNode;
  aside?: ReactNode;
  intro?: ReactNode;
  // Immersive mode (Pavel 2026-07-23): the delivered page fills the WHOLE
  // screen with its own brand chrome; documents render as a centered sheet.
  immersive?: boolean;
  panelLabel?: string;
  // While a walk recording runs, a glowing pointer follows the cursor over
  // the reading surface so the client SEES what they are pointing at.
  walkActive?: boolean;
  // Partner workflow panel (Alex queue) keeps its column layout inside.
  workflow?: boolean;
  // "Mluv a ukazuj" capture: the reading surface reports pointer moves so the
  // walk recorder can pair the voice with the pointed-at passage.
  onContentPointerMove?: (event: React.PointerEvent<HTMLDivElement>) => void;
  // Documents offer "Uložit jako PDF" (native print-to-PDF); image viewers
  // and immersive pages do not.
  printable?: boolean;
  onReadProgress?: (state: { percent: number; atEnd: boolean }) => void;
  closeButtonRef?: Ref<HTMLButtonElement>;
}) {
  const dotRef = useRef<HTMLDivElement | null>(null);
  // F97 (Pavel 2026-08-07): while a material fills the screen, no floating
  // message may land on top of it. This overlay is the ONE component behind
  // every full-screen material - the four client documents, the sales page
  // review room, the avatar viewer, the partner's review room - so the fact
  // is registered here rather than restated on each of those surfaces.
  // Consumed by src/toast-layer.tsx.
  useEffect(() => registerOpenDocument(), []);
  // On phones the panel starts as the pill: the material is the hero, the
  // tools are one tap away instead of covering half the screen.
  const [panelOpen, setPanelOpen] = useState(() => typeof window === "undefined" || window.innerWidth > 720);
  const canDock = typeof window !== "undefined" && window.innerWidth > 1100;
  const [docked, setDocked] = useState<boolean>(() => {
    if (!canDock) return false;
    try { return window.localStorage.getItem("launchpad-alex-panel-dock") !== "0"; } catch { return true; }
  });
  function setDock(next: boolean) {
    setDocked(next);
    try { window.localStorage.setItem("launchpad-alex-panel-dock", next ? "1" : "0"); } catch { /* soukromy rezim */ }
  }
  // Drag & drop for the floating panel (Pavel 2026-07-23): grab the header
  // and move the panel wherever it covers the least.
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  // Chapter navigation + reading progress (T6a): the rendered document is
  // the source of truth - a DOM scan finds the chapter openers, a scroll
  // listener keeps the active chapter and the progress bar honest.
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  const scrollRaf = useRef(0);
  const [chapters, setChapters] = useState<Array<{ title: string; el: HTMLElement }>>([]);
  const [activeChapter, setActiveChapter] = useState(0);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const dockActive = docked && panelOpen && Boolean(props.aside);
  useEffect(() => {
    if (props.immersive) return;
    const root = contentRef.current;
    if (!root) return;
    let lastSignature = "";
    const scan = () => {
      const found = [...root.querySelectorAll("h3.doc-section")]
        .filter((el) => !el.closest("details") && !el.closest(".doc-diff-del"))
        .map((el) => ({ title: chapterHeadingTitle(el), el: el as HTMLElement }));
      const signature = found.map((chapter) => chapter.title).join("\u0000");
      if (signature !== lastSignature) { lastSignature = signature; setChapters(found); }
    };
    scan();
    // Version switches and diff toggles swap the content under us; the
    // observer re-scans instead of threading props through every caller.
    const observer = new MutationObserver(scan);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [props.immersive]);
  useEffect(() => () => { if (scrollRaf.current) window.cancelAnimationFrame(scrollRaf.current); }, []);
  const handleDocScroll = () => {
    if (scrollRaf.current) return;
    scrollRaf.current = window.requestAnimationFrame(() => {
      scrollRaf.current = 0;
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const state = readerProgressState(scroller.scrollTop, scroller.scrollHeight, scroller.clientHeight);
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${state.percent / 100})`;
      }
      props.onReadProgress?.(state);
      if (chapters.length > 1) {
        const scrollerTop = scroller.getBoundingClientRect().top;
        const tops = chapters.map((chapter) => chapter.el.getBoundingClientRect().top - scrollerTop + scroller.scrollTop);
        const index = state.atEnd ? chapters.length - 1 : activeChapterIndex(tops, scroller.scrollTop + 160);
        setActiveChapter((previous) => (previous === index ? previous : index));
      }
    });
  };
  // Initial paint and every geometry change: docking, responsive reflow and
  // font/content layout can change the end without producing a scroll event.
  useEffect(() => {
    handleDocScroll();
    if (props.immersive || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => handleDocScroll());
    if (scrollerRef.current) observer.observe(scrollerRef.current);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [chapters, dockActive, props.immersive]);  // eslint-disable-line react-hooks/exhaustive-deps
  const jumpToChapter = (index: number) => {
    setChaptersOpen(false);
    const heading = chapters[index]?.el;
    if (!heading) return;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
    heading.addEventListener("blur", () => heading.removeAttribute("tabindex"), { once: true });
    heading.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  useEffect(() => {
    if (props.immersive) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() => scrollerRef.current?.focus({ preventScroll: true }));
    return () => {
      window.cancelAnimationFrame(frame);
      opener?.focus();
    };
  }, [props.immersive]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") props.onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [props]);
  // Cmd+P and the sheet button share one path: beforeprint prepares the
  // sheet (root class + folds open), afterprint restores exactly what it did.
  const printRestoreRef = useRef<(() => void) | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  useEffect(() => {
    if (props.immersive) return;
    const before = () => { printRestoreRef.current?.(); printRestoreRef.current = beginDocPrint(document as unknown as PrintRoot); };
    const after = () => { printRestoreRef.current?.(); printRestoreRef.current = null; };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => { after(); window.removeEventListener("beforeprint", before); window.removeEventListener("afterprint", after); };
  }, [props.immersive]);
  const moveDot = (event: React.PointerEvent<HTMLDivElement>) => {
    props.onContentPointerMove?.(event);
    moveWalkCursorOrb(dotRef.current, props.walkActive === true, event.clientX, event.clientY);
  };
  const panel = props.aside && (panelOpen ? (
    <aside
      className={`doc-float-panel${props.workflow ? " workflow" : ""}${dockActive ? " docked" : ""}`}
      style={!dockActive && panelPos ? { left: panelPos.x, top: panelPos.y, right: "auto" } : undefined}
    >
      <div
        className={`doc-float-panel-head${dockActive ? "" : " draggable"}`}
        onPointerDown={(event) => {
          if (dockActive) return;
          if (event.target instanceof Element && event.target.closest("button")) return;
          const panel = event.currentTarget.parentElement;
          if (!panel) return;
          const rect = panel.getBoundingClientRect();
          dragRef.current = { dx: event.clientX - rect.left, dy: event.clientY - rect.top };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!dragRef.current) return;
          setPanelPos({
            x: Math.min(Math.max(8, event.clientX - dragRef.current.dx), window.innerWidth - 140),
            y: Math.min(Math.max(8, event.clientY - dragRef.current.dy), window.innerHeight - 60)
          });
        }}
        onPointerUp={() => { dragRef.current = null; }}
      >
        <strong>{props.panelLabel}</strong>
        <span className="doc-float-panel-controls">
          {canDock && (
            <button
              className="quiet-link panel-dock-toggle"
              type="button"
              data-panel-control="dock"
              aria-pressed={dockActive}
              aria-label={props.t(dockActive ? "reader.panelFloat" : "reader.panelPin")}
              title={props.t(dockActive ? "reader.panelFloat" : "reader.panelPin")}
              onClick={() => setDock(!dockActive)}
            >
              {dockActive ? (
                <svg className="panel-control-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <rect x="3.5" y="4.5" width="12" height="11" rx="2" />
                  <rect x="8.5" y="8.5" width="12" height="11" rx="2" />
                </svg>
              ) : (
                <svg className="panel-control-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
                  <path d="M16.5 4.5v15M7.5 12h6m-3-3 3 3-3 3" />
                </svg>
              )}
            </button>
          )}
        </span>
      </div>
      {/* Review instructions are APP chrome: inside the sheet they printed
          into the PDF and read as part of the deliverable (Pavel 2026-08-04).
          They live with the controls they explain. */}
      {props.intro}
      {props.aside}
    </aside>
  ) : (
    <button className="secondary-button doc-float-panel-pill" type="button" onClick={() => setPanelOpen(true)}>
      {props.panelLabel}
    </button>
  ));
  // Portal to <body>: an ancestor with transform/filter would otherwise turn
  // position:fixed into ancestor-relative and shrink the overlay to the tile.
  return createPortal(
    <div
      className={`doc-overlay${props.immersive ? " immersive" : " doc-mode"}${dockActive ? " dock-active" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={props.title}
    >
      {props.immersive ? (
        // No title chip: the delivered page shows its OWN logo and menu
        // untouched (Pavel 2026-07-23); the only chrome is the close cross
        // and the panel.
        <div
          className={`doc-overlay-stage${props.walkActive ? " walk-live" : ""}`}
          onPointerMove={moveDot}
          onPointerDown={moveDot}
        >
          {props.children}
        </div>
      ) : (
        // Documents read as a centered sheet whose masthead is part of the
        // document itself; the sheet shrinks beside the pinned panel.
        <div
          className="doc-overlay-doc"
          ref={scrollerRef}
          onScroll={handleDocScroll}
          tabIndex={0}
          aria-label={props.title}
        >
          <div className="doc-sheet">
            <header className="doc-sheet-head">
              <div>
                <p className="card-kicker">{props.kicker}</p>
                <h2>{props.title}</h2>
                {props.meta && <p className="doc-sheet-meta">{props.meta}</p>}
              </div>
              <div className="doc-sheet-tools">
                {chapters.length > 1 && (
                  <button
                    className="quiet-link doc-chapters-toggle"
                    type="button"
                    aria-expanded={chaptersOpen}
                    onClick={() => setChaptersOpen((open) => !open)}
                  >
                    {props.t("reader.chapters")}
                  </button>
                )}
                {props.printable && (
                  <button
                    className="quiet-link doc-print-link"
                    type="button"
                    disabled={pdfBusy}
                    onClick={() => {
                      setPdfBusy(true);
                      void downloadDocPdf(props.title).then((delivered) => {
                        setPdfBusy(false);
                        if (!delivered) window.print();
                      });
                    }}
                  >
                    {pdfBusy ? props.t("reader.printingPdf") : props.t("reader.printPdf")}
                  </button>
                )}
              </div>
              {chaptersOpen && (
                <div className="doc-chapters-pop">
                  <ChapterList chapters={chapters} active={activeChapter} label={props.t("reader.chapters")} onSelect={jumpToChapter} />
                </div>
              )}
            </header>
            <div
              className={`doc-overlay-content${props.walkActive ? " walk-live" : ""}`}
              ref={contentRef}
              onPointerMove={moveDot}
              onPointerDown={moveDot}
            >
              {props.children}
            </div>
          </div>
        </div>
      )}
      {!props.immersive && (
        <div className="doc-progress" aria-hidden="true"><div className="doc-progress-fill" ref={progressRef} /></div>
      )}
      {!props.immersive && chapters.length > 1 && (
        <div className="doc-chapter-rail">
          <ChapterList chapters={chapters} active={activeChapter} label={props.t("reader.chapters")} onSelect={jumpToChapter} />
        </div>
      )}
      <button ref={props.closeButtonRef} className="doc-overlay-close doc-float-close" type="button" onClick={() => props.onClose()} aria-label={props.closeLabel}>{"\u00d7"}</button>
      {panel}
      {props.walkActive && <div ref={dotRef} className="walk-cursor-dot" aria-hidden="true" />}
    </div>,
    document.body
  );
}

// The block kinds a pointed-at anchor can bind to - shared by the walk
// capture and the note-chip highlighter, so what is captured can be found.
// Authored-HTML layouts add their own leaf blocks (cards, persona scenes,
// short quotes, timeline steps, swatches). They must be anchorable too, or the
// client can point at a card while speaking and the note lands nowhere.
// The interactive leaves (2026-08-06) are on the list for the same reason and
// with one extra stake: a copy field IS the subject line, and a timeline beat
// IS the e-mail Pavel wants to talk about, so those are the blocks a walk
// comment is most likely to be aimed at.
const ANCHOR_BLOCK_SELECTOR = "p, li, h2, h3, h4, td, th, blockquote, summary, .doc-callout, .doc-stat, .doc-hero, .doc-meter, .doc-card, .doc-scene, .doc-step, .doc-swatch, .doc-copyfield, .doc-timeline-beat, .doc-segments-item, .doc-map-row, .doc-fstage, .doc-wf-frame, .doc-evening-item, .doc-ph-head, .doc-mi";

// Readable text of an anchored block. Stat tiles are stacked spans whose
// textContent glues together ("8Let finančním ředitelemstrojírna...");
// join their parts with an interpunct so the snippet reads cleanly and the
// highlighter can match it (orchestrator walk 2026-07-24, finding 3).
// The tile CTA models the invited action, not just "open": a document waiting
// for the client's comments says so ("Prohlédnout a komentovat"), same as the
// sales-page card - anything else understates the client's most valuable
// step (night R29). Exported for tests.
export function docTileCtaKey(status: string, canAct: boolean): string {
  return canAct && status === "review" ? "sales.doc.review" : "sales.doc.read";
}

export function anchorSnippet(element: Element): string {
  // Stat tiles, key-number heroes and gauge rows are stacked spans - joined
  // with an interpunct so the snippet reads cleanly (walk finding 3).
  const stacked = ["doc-stat", "doc-hero", "doc-meter"].some((name) => element.classList.contains(name));
  const parts = stacked
    ? [...element.children].map((child) => child.textContent?.trim() ?? "").filter(Boolean)
    : [];
  const text = parts.length > 0 ? parts.join(" · ") : element.textContent ?? "";
  return text.replace(/\s+/g, " ").trim();
}

// Document-relative walk point: x/y in percent of the full reading surface,
// fact = the text of the block under the cursor (the anchor the agent needs).
export function walkPointFromEvent(event: React.PointerEvent<HTMLDivElement>): Omit<WalkPoint, "t"> {
  const rect = event.currentTarget.getBoundingClientRect();
  const target = event.target instanceof Element ? event.target.closest(ANCHOR_BLOCK_SELECTOR) : null;
  const fact = target ? anchorSnippet(target).slice(0, 140) || undefined : undefined;
  return {
    x: Math.round(((event.clientX - rect.left) / Math.max(rect.width, 1)) * 1000) / 10,
    y: Math.round(((event.clientY - rect.top) / Math.max(rect.height, 1)) * 1000) / 10,
    fact
  };
}

// Where to scroll so the anchored block centers in the UNOBSCURED part of
// the viewport: on mobile the notes panel is a bottom sheet, so centering
// against the full viewport would park the passage behind it. Pure math,
// exported for tests. Returns the scrollBy delta.
export function anchorScrollDelta(elTop: number, elHeight: number, viewportHeight: number, obscuredBottom: number): number {
  const visible = Math.max(viewportHeight - obscuredBottom, 120);
  if (elHeight >= visible - 32) return elTop - 16;
  return elTop - (visible / 2 - elHeight / 2);
}

// Height of the viewport strip covered by an open bottom-sheet panel (mobile
// reader). Desktop float/dock panels do not cover the bottom, so they report 0.
function obscuredBottomHeight(): number {
  const panel = document.querySelector(".doc-float-panel");
  if (!panel) return 0;
  const rect = panel.getBoundingClientRect();
  const coversWidth = rect.width >= window.innerWidth * 0.9;
  const sitsAtBottom = Math.abs(rect.bottom - window.innerHeight) < 2;
  if (!coversWidth || !sitsAtBottom || rect.top <= 0) return 0;
  return window.innerHeight - rect.top;
}

// Interactive anchors (Pavel 2026-07-24): hovering a note's chip lights up
// the passage it points at; clicking scrolls the document there and cycles
// through multiple anchors. Text-match on the captured snippet (documents
// render in our own DOM). Shared by the client reader and the partner panel.
export function useAnchorFocus(): (noteId: string, anchors: string[], scroll: boolean) => void {
  const cycleRef = useRef<{ id: string; index: number }>({ id: "", index: 0 });
  const flashTimer = useRef(0);
  return useCallback((noteId: string, anchors: string[], scroll: boolean) => {
    const root = document.querySelector(".doc-overlay-content");
    if (!root || anchors.length === 0) return;
    let index = 0;
    if (scroll) {
      index = cycleRef.current.id === noteId ? (cycleRef.current.index + 1) % anchors.length : 0;
      cycleRef.current = { id: noteId, index };
    }
    const blocks = [...root.querySelectorAll(ANCHOR_BLOCK_SELECTOR)];
    const norm = (value: string) => value.replace(/\s+/g, " ").trim();
    // The captured snippet may differ from the CURRENT version (Alex edited
    // it since) - try the picked anchor first, then the rest, long match
    // before short. Older notes captured stat tiles WITHOUT separators, so
    // the raw glued textContent stays a fallback haystack.
    const candidates = [anchors[index], ...anchors].filter(Boolean);
    for (const candidate of candidates) {
      for (const length of [70, 40, 24]) {
        const needle = norm(candidate).slice(0, length);
        if (needle.length < 12) continue;
        const el = blocks.find((block) =>
          anchorSnippet(block).includes(needle) || norm(block.textContent ?? "").includes(needle));
        if (el) {
          window.clearTimeout(flashTimer.current);
          for (const lit of root.querySelectorAll(".anchor-flash")) lit.classList.remove("anchor-flash");
          el.closest("details")?.setAttribute("open", "");
          el.classList.add("anchor-flash");
          if (scroll) {
            const obscured = obscuredBottomHeight();
            const scroller = document.querySelector(".doc-overlay-doc");
            if (obscured > 0 && scroller) {
              const rect = el.getBoundingClientRect();
              scroller.scrollBy({ top: anchorScrollDelta(rect.top, rect.height, window.innerHeight, obscured), behavior: "smooth" });
            } else {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }
          flashTimer.current = window.setTimeout(() => el.classList.remove("anchor-flash"), 2200);
          return;
        }
      }
    }
  }, []);
}

// The ◎ N chip on a note: hover or keyboard focus highlights the pointed-at
// passage, click scrolls to it (and cycles when the note has several).
export function AnchorChip(props: {
  count: number;
  label: string;
  visibleLabel?: string;
  onFocus: () => void;
  onActivate: () => void;
}) {
  return (
    <button
      className="anchor-chip"
      type="button"
      title={props.label}
      aria-label={props.label}
      onMouseEnter={props.onFocus}
      onFocus={props.onFocus}
      onClick={props.onActivate}
    >
      {props.visibleLabel ?? `◎ ${props.count}`}
    </button>
  );
}

// One timestamp format for every note card, client and partner alike. F116:
// the locale comes from `doc-locale`, the same read the renderer's own chrome
// uses - a dozen call sites pass no translator, and threading one through all
// of them to date a note would be a change to every caller for one leaf.
export function formatNoteTime(iso: string): string {
  return formatMediumDateTime(iso, docLocale());
}

// First real paragraph of the document (the {.lead} standfirst) = tile teaser.
export function docExcerpt(md: string): string {
  // An authored-HTML document has no plain first line; reading it the markdown
  // way puts raw tags on the document tile.
  if (isDocHtml(md)) {
    const lead = /<lead[^>]*>([\s\S]*?)<\/lead>/i.exec(md) ?? /<p[^>]*>([\s\S]*?)<\/p>/i.exec(md);
    return lead ? lead[1].replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim() : "";
  }
  for (const raw of md.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line || /^[#>|!:?]/.test(line) || line.startsWith("???") || line.startsWith(":::")) continue;
    return line.replace(/\s*\{\.lead\}\s*$/, "").replace(/\*\*/g, "");
  }
  return "";
}
